"use client";

import { Tokens } from "~/services/api/types/tokens";
import { User } from "~/services/api/types/user";
import {
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    AuthActionsContext,
    AuthContext,
    AuthTokensContext,
    TokensInfo,
} from "./auth-context";
import Cookies from "js-cookie";
import useFetchBase from "~/services/api/use-fetch-base";
import { AUTH_LOGOUT_URL } from "~/services/api/config";
import { ALL_ME_RELATIONS, buildAuthMeUrl } from "~/services/api/services/auth";
import HTTP_CODES_ENUM from "../api/types/http-codes";
import { STORAGE_KEYS } from "~/types/Storage";
import { shutdown } from "~/lib/intercom/IntercomScript";
import useUserId from "~/hooks/useUserId";
import { removeStorageItem } from "~/lib/storage/Storage";
import { AUTH_TOKEN_COOKIE, AUTH_TOKEN_COOKIE_EXPIRES_DAYS } from "./auth-cookie";

function AuthProvider(props: PropsWithChildren<object>) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const { setUserId, resetUserId } = useUserId();
    const tokensInfoRef = useRef<Tokens>({
        token: null,
        refreshToken: null,
        tokenExpires: null,
    });
    const fetchBase = useFetchBase();

    const setTokensInfoRef = useCallback((tokens: TokensInfo) => {
        tokensInfoRef.current = tokens ?? {
            token: null,
            refreshToken: null,
            tokenExpires: null,
        };
    }, []);

    const setTokensInfo = useCallback(
        (tokensInfo: TokensInfo) => {
            setTokensInfoRef(tokensInfo);

            if (tokensInfo) {
                Cookies.set(AUTH_TOKEN_COOKIE, JSON.stringify(tokensInfo), {
                    expires: AUTH_TOKEN_COOKIE_EXPIRES_DAYS,
                });
                setIsSessionExpired(false);
            } else {
                Cookies.remove(AUTH_TOKEN_COOKIE);
                setUser(null);
            }
        },
        [setTokensInfoRef]
    );

    const logOut = useCallback(async () => {
        // The session is torn down before the POST so a 401 arriving
        // meanwhile no longer matches the current token in `onUnauthorized`.
        const { token, refreshToken, tokenExpires } = tokensInfoRef.current;
        setTokensInfo(null);
        removeStorageItem(STORAGE_KEYS.USER_ID);
        shutdown();
        resetUserId();

        if (token) {
            await fetchBase(
                AUTH_LOGOUT_URL,
                {
                    method: "POST",
                },
                { token, refreshToken, tokenExpires }
            );
        }
    }, [setTokensInfo, fetchBase, resetUserId]);

    // A request sent before a logout or re-login can be rejected after it;
    // its 401 is about a session that is already gone.
    const onUnauthorized = useCallback(
        (rejectedToken: string) => {
            if (tokensInfoRef.current.token !== rejectedToken) return;
            setIsSessionExpired(true);
            logOut();
        },
        [logOut]
    );

    const loadData = useCallback(async () => {
        const tokens = JSON.parse(
            Cookies.get(AUTH_TOKEN_COOKIE) ?? "null"
        ) as TokensInfo;
        setIsLoading(true);

        setTokensInfoRef(tokens);

        // A logout or re-login while `/me` is in flight replaces the token;
        // the response then belongs to a session that is gone.
        const isSessionCurrent = () =>
            tokensInfoRef.current.token === tokens?.token;

        try {
            if (tokens?.token) {
                const response = await fetchBase(
                    buildAuthMeUrl(ALL_ME_RELATIONS),
                    {
                        method: "GET",
                    },
                    {
                        token: tokens.token,
                        refreshToken: tokens.refreshToken,
                        tokenExpires: tokens.tokenExpires,
                        onUnauthorized,
                    }
                );

                // A timed-out / offline / aborted request resolves to
                // `undefined` (the total fetch contract — see use-fetch-base.ts),
                // NOT to a `Response`. That is a transient network failure, not
                // an auth rejection: keep the session and just finish loading so
                // a momentary connection drop (e.g. a slow FB/IG webview on app
                // open) never silently logs the user out. Only an explicit 401
                // tears the session down, through `onUnauthorized`.
                if (!response || !isSessionCurrent()) {
                    return;
                }

                if (response.status === HTTP_CODES_ENUM.UNAUTHORIZED) {
                    return;
                }

                // An unreadable body (e.g. a proxy's HTML error page) is not an
                // auth rejection either, so it must not reach the logout below.
                const data = await response.json().catch(() => null);
                if (!data || !isSessionCurrent()) {
                    return;
                }
                setUser(data);
                setUserId(data.id);
            }
        } catch {
            if (isSessionCurrent()) {
                logOut();
            }
        }
        finally {
            setIsLoaded(true);
            setIsLoading(false);
        }
    }, [
        fetchBase,
        logOut,
        onUnauthorized,
        setTokensInfoRef,
        setUserId,
    ]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const contextValue = useMemo(
        () => ({
            isLoaded,
            isLoading,
            user,
            isSessionExpired,
        }),
        [isLoaded, isLoading, user, isSessionExpired]
    );

    const contextActionsValue = useMemo(
        () => ({
            setUser,
            logOut,
        }),
        [logOut]
    );

    const contextTokensValue = useMemo(
        () => ({
            tokensInfoRef,
            setTokensInfo,
            onUnauthorized,
        }),
        [setTokensInfo, onUnauthorized]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            <AuthActionsContext.Provider value={contextActionsValue}>
                <AuthTokensContext.Provider value={contextTokensValue}>
                    {props.children}
                </AuthTokensContext.Provider>
            </AuthActionsContext.Provider>
        </AuthContext.Provider>
    );
}

export default AuthProvider;
